import { ArrowDown, ArrowSquareOut, ArrowUp, DotsSixVertical, DotsThree, Eye, Plus } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemCreate, ItemLite, ItemPatch } from "@plannello/shared";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  DropIndicator,
  type DropItem,
  type DropTarget,
  GridList,
  GridListItem,
  isTextDropItem,
  ListLayout,
  useDragAndDrop,
  Virtualizer,
} from "react-aria-components";
import { IconButton, Menu, MenuItem, MenuSection, MenuSeparator, MenuTrigger, SubmenuTrigger } from "@/components/ui";
import type { Catalog } from "@/data/catalog";
import { rankBetweenItems, updateItems } from "@/data/mutations";
import { useTitles } from "@/data/titles";
import { cx } from "@/lib/cx";
import { useRegisterView } from "./activeView";
import type { ChildStats } from "./display";
import type { Group, GroupBy } from "./grouping";
import { ItemCard } from "./ItemCard";
import { useReadOnly } from "./readOnly";

const DRAG_TYPE = "application/x-plannello-item";

export interface BoardProps {
  groups: Group[];
  groupBy: GroupBy;
  catalog: Catalog;
  childStats: Map<string, ChildStats>;
  label: string;
  peekKey: string | null;
  /** Cards keep manual order only when the view sorts by rank; otherwise reordering is off. */
  rankSorted?: boolean;
  onOpen: (item: ItemLite) => void;
  onPeek: (item: ItemLite) => void;
  onEscape?: () => void;
  createDefaults?: (group: Group | null) => Partial<ItemCreate>;
  onCreateInGroup?: (group: Group) => void;
}

/**
 * Board: one React Aria GridList per column, so cards get grid keyboard navigation, drag and drop
 * with keyboard and screen reader support (drag handle per card, announcements), and drop
 * indicators. Non-drag alternatives (WCAG 2.5.7): S and the other property keys, the card menu's
 * "Move to", Alt+Left/Right to the neighbouring column and Alt+Up/Down to reorder.
 */
export function Board({
  groups,
  groupBy,
  catalog,
  childStats,
  label,
  peekKey,
  rankSorted = true,
  onOpen,
  onPeek,
  onEscape,
  createDefaults,
  onCreateInGroup,
}: BoardProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const index = useMemo(() => {
    const byId = new Map<string, { item: ItemLite; group: Group; pos: number }>();
    for (const g of groups) {
      g.items.forEach((item, pos) => {
        byId.set(item.id, { item, group: g, pos });
      });
    }
    return byId;
  }, [groups]);

  const cardEl = (id: string) =>
    rootRef.current
      ?.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(id)}"]`)
      ?.closest<HTMLElement>('[role="row"]') ?? null;

  // After a keyboard move the card lives in another GridList: put focus back on it once React
  // Aria has finished moving focus inside the old column (it focuses the next card on removal).
  const focusSoon = (id: string) => {
    let tries = 0;
    const tick = () => {
      const el = cardEl(id);
      if (el && document.activeElement !== el) el.focus();
      if (++tries < 6) setTimeout(tick, 30);
    };
    setTimeout(tick, 0);
  };

  /** Moves items into `group`, between the neighbours of `target` (or at the end). */
  function moveInto(group: Group, ids: string[], target: DropTarget | null, opts: { focus?: boolean } = {}) {
    const items = ids.map((id) => index.get(id)?.item).filter((i): i is ItemLite => !!i);
    if (items.length === 0) return;
    const list = group.items.filter((i) => !ids.includes(i.id));
    let before: ItemLite | undefined;
    let after: ItemLite | undefined;
    if (target && target.type === "item") {
      const at = list.findIndex((i) => i.id === String(target.key));
      if (at >= 0) {
        if (target.dropPosition === "before") {
          before = list[at - 1];
          after = list[at];
        } else {
          before = list[at];
          after = list[at + 1];
        }
      }
    } else {
      before = list[list.length - 1];
    }
    const ranks = new Map<string, string>();
    let prev = before;
    for (const item of items) {
      const rank = rankBetweenItems(prev, after);
      ranks.set(item.id, rank);
      prev = { ...item, rank };
    }
    const sameGroup = items.every((i) => index.get(i.id)?.group.key === group.key);
    void updateItems(
      items,
      (item): ItemPatch | null => {
        const move = group.patchFor(item) ?? {};
        const patch: ItemPatch = { ...move };
        if (rankSorted && (target || !sameGroup)) patch.rank = ranks.get(item.id);
        return Object.keys(patch).length ? patch : null;
      },
      { undoTitle: sameGroup ? m.toast_reordered() : m.toast_moved({ column: group.label }) },
    );
    if (opts.focus) focusSoon(items[0]!.id);
  }

  function reorder(item: ItemLite, delta: -1 | 1) {
    const at = index.get(item.id);
    if (!at || !rankSorted) return;
    const neighbour = at.group.items[at.pos + delta];
    if (!neighbour) return;
    moveInto(
      at.group,
      [item.id],
      { type: "item", key: neighbour.id, dropPosition: delta < 0 ? "before" : "after" },
      { focus: true },
    );
  }

  function moveColumn(item: ItemLite, delta: -1 | 1) {
    const at = index.get(item.id);
    if (!at) return;
    const gi = groups.findIndex((g) => g.key === at.group.key);
    const next = groups[gi + delta];
    if (next) moveInto(next, [item.id], null, { focus: true });
  }

  const focused = () => (focusedId ? (index.get(focusedId)?.item ?? null) : null);

  useRegisterView({
    kind: "board",
    root: () => rootRef.current,
    targets: () => {
      const f = focused();
      return f ? [f] : [];
    },
    anchor: () => (focusedId ? cardEl(focusedId) : null) ?? rootRef.current,
    move: (d) => {
      const f = focused();
      const at = f ? index.get(f.id) : undefined;
      const target = at ? at.group.items[at.pos + d] : groups.find((g) => g.items.length)?.items[0];
      if (target) cardEl(target.id)?.focus();
    },
    open: onOpen,
    peek: onPeek,
    createDefaults: () => createDefaults?.(focusedId ? (index.get(focusedId)?.group ?? null) : null) ?? {},
    restoreFocus: () => {
      if (focusedId) cardEl(focusedId)?.focus();
    },
  });

  const onKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    const row = (e.target as HTMLElement).closest?.('[role="row"]');
    const id = row?.querySelector<HTMLElement>("[data-item-id]")?.dataset.itemId;
    const item = id ? index.get(id)?.item : undefined;
    if (!item || row !== e.target) {
      if (e.key === "Escape" && onEscape) onEscape();
      return;
    }
    let handled = true;
    if (e.altKey && e.key === "ArrowUp") reorder(item, -1);
    else if (e.altKey && e.key === "ArrowDown") reorder(item, 1);
    else if (e.altKey && e.key === "ArrowLeft") moveColumn(item, -1);
    else if (e.altKey && e.key === "ArrowRight") moveColumn(item, 1);
    else if (e.key === " " && !e.altKey && !e.metaKey && !e.ctrlKey) onPeek(item);
    else if (e.key === "Escape" && onEscape) onEscape();
    else handled = false;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: a labelled group of board columns, not a form fieldset
    <div
      ref={rootRef}
      role="group"
      aria-label={label}
      onKeyDownCapture={onKeyDownCapture}
      onFocusCapture={(e) => {
        const row = (e.target as HTMLElement).closest?.('[role="row"]');
        const id = row?.querySelector<HTMLElement>("[data-item-id]")?.dataset.itemId;
        if (id) setFocusedId(id);
      }}
      className="scroll-thin flex min-h-0 flex-1 gap-3 overflow-x-auto px-5 pb-4 max-md:snap-x max-md:snap-mandatory max-md:scroll-px-5 sm:px-8"
    >
      {groups.map((g, gi) => (
        <BoardColumn
          key={g.key}
          group={g}
          groups={groups}
          columnIndex={gi}
          catalog={catalog}
          childStats={childStats}
          showStatus={groupBy !== "status"}
          peekKey={peekKey}
          rankSorted={rankSorted}
          onOpen={onOpen}
          onPeek={onPeek}
          onMove={moveInto}
          onReorder={reorder}
          onCreate={onCreateInGroup}
          itemById={(id) => index.get(id)?.item}
        />
      ))}
    </div>
  );
}

function BoardColumn({
  group,
  groups,
  columnIndex,
  catalog,
  childStats,
  showStatus,
  peekKey,
  rankSorted,
  onOpen,
  onPeek,
  onMove,
  onReorder,
  onCreate,
  itemById,
}: {
  group: Group;
  groups: Group[];
  columnIndex: number;
  catalog: Catalog;
  childStats: Map<string, ChildStats>;
  showStatus: boolean;
  peekKey: string | null;
  rankSorted: boolean;
  onOpen: (item: ItemLite) => void;
  onPeek: (item: ItemLite) => void;
  onMove: (group: Group, ids: string[], target: DropTarget | null, opts?: { focus?: boolean }) => void;
  onReorder: (item: ItemLite, delta: -1 | 1) => void;
  onCreate?: (group: Group) => void;
  itemById: (id: string) => ItemLite | undefined;
}) {
  const readOnly = useReadOnly();
  const titleOf = useTitles();
  const headingId = `col-${columnIndex}`;
  const readIds = async (items: DropItem[]) =>
    Promise.all(
      items
        .filter(isTextDropItem)
        .filter((i) => i.types.has(DRAG_TYPE))
        .map((i) => i.getText(DRAG_TYPE)),
    );

  const { dragAndDropHooks } = useDragAndDrop<ItemLite>({
    getItems: (keys) =>
      [...keys].map((k) => ({ [DRAG_TYPE]: String(k), "text/plain": itemById(String(k))?.key ?? String(k) })),
    acceptedDragTypes: [DRAG_TYPE],
    getDropOperation: () => "move",
    onReorder: rankSorted ? (e) => onMove(group, [...e.keys].map(String), e.target) : undefined,
    onInsert: async (e) => onMove(group, await readIds(e.items), e.target),
    onRootDrop: async (e) => onMove(group, await readIds(e.items), null),
    renderDropIndicator: (target) => (
      <DropIndicator
        target={target}
        className="relative h-0 outline-none data-drop-target:before:absolute data-drop-target:before:inset-x-1 data-drop-target:before:-top-[3px] data-drop-target:before:h-0.5 data-drop-target:before:rounded-full data-drop-target:before:bg-accent"
      />
    ),
  });

  const layout = useMemo(() => new ListLayout({ estimatedRowHeight: 88, gap: 6, padding: 2 }), []);

  return (
    <section
      aria-labelledby={headingId}
      className="flex w-[288px] shrink-0 snap-start flex-col border-t-2 border-transparent max-md:w-[calc(100vw-48px)]"
      style={group.accent ? { borderTopColor: group.accent } : undefined}
    >
      <header className="group/col flex h-10 shrink-0 items-center gap-2 px-1.5">
        {group.icon ? <span className="flex w-4 shrink-0 justify-center">{group.icon}</span> : null}
        <h2 id={headingId} className="truncate-title text-sm font-medium text-fg">
          {group.label}
        </h2>
        <span className="text-xs text-fg-subtle tnum">{group.items.length}</span>
        <span className="flex-1" />
        {onCreate ? (
          <IconButton
            aria-label={`${m.action_create_item()}: ${group.label}`}
            icon={Plus}
            size="sm"
            iconSize={14}
            onPress={() => onCreate(group)}
            className="opacity-0 group-hover/col:opacity-100 focus-visible:opacity-100"
          />
        ) : null}
      </header>
      <Virtualizer layout={layout}>
        <GridList
          aria-labelledby={headingId}
          items={group.items}
          dragAndDropHooks={readOnly ? undefined : dragAndDropHooks}
          onAction={(key) => {
            const item = itemById(String(key));
            if (item) onOpen(item);
          }}
          renderEmptyState={() => (
            <div className="flex h-24 items-center justify-center rounded-panel border border-dashed border-line text-xs text-fg-subtle">
              {m.column_empty()}
            </div>
          )}
          className={cx(
            "scroll-thin min-h-24 flex-1 overflow-y-auto rounded-panel outline-none",
            "data-drop-target:bg-hover data-drop-target:outline-2 data-drop-target:outline-dashed data-drop-target:outline-accent",
          )}
        >
          {(item) => (
            <GridListItem
              id={item.id}
              textValue={titleOf(item).text}
              className={cx(
                "group/card relative cursor-default rounded-panel border bg-surface p-2.5 outline-hidden transition-ui",
                "shadow-[0_1px_2px_var(--shadow-soft)] data-hovered:border-line-strong",
                "data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-offset-1 data-focus-visible:outline-focus",
                "data-dragging:opacity-40",
                peekKey === item.key ? "border-accent" : "border-line",
              )}
            >
              <ItemCard item={item} catalog={catalog} stats={childStats.get(item.id)} showStatus={showStatus} />
              {readOnly ? null : (
                <div className="absolute top-2 right-9 flex items-center gap-0.5 opacity-0 transition-ui group-data-hovered/card:opacity-100 group-data-focus-visible/card:opacity-100 group-focus-within/card:opacity-100">
                  <AriaButton
                    slot="drag"
                    aria-label={m.board_move_to()}
                    className="flex size-6 cursor-grab items-center justify-center rounded-control bg-surface text-fg-subtle outline-hidden data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus data-hovered:bg-hover"
                  >
                    <DotsSixVertical size={14} aria-hidden="true" />
                  </AriaButton>
                  <CardMenu
                    item={item}
                    group={group}
                    groups={groups}
                    rankSorted={rankSorted}
                    onOpen={onOpen}
                    onPeek={onPeek}
                    onMove={onMove}
                    onReorder={onReorder}
                  />
                </div>
              )}
            </GridListItem>
          )}
        </GridList>
      </Virtualizer>
    </section>
  );
}

function CardMenu({
  item,
  group,
  groups,
  rankSorted,
  onOpen,
  onPeek,
  onMove,
  onReorder,
}: {
  item: ItemLite;
  group: Group;
  groups: Group[];
  rankSorted: boolean;
  onOpen: (item: ItemLite) => void;
  onPeek: (item: ItemLite) => void;
  onMove: (group: Group, ids: string[], target: DropTarget | null, opts?: { focus?: boolean }) => void;
  onReorder: (item: ItemLite, delta: -1 | 1) => void;
}) {
  return (
    <MenuTrigger>
      <IconButton aria-label={m.common_more()} icon={DotsThree} size="sm" noTooltip className="bg-surface" />
      <Menu aria-label={m.common_more()} placement="bottom end">
        <MenuItem id="open" icon={ArrowSquareOut} shortcut={["enter"]} onAction={() => onOpen(item)}>
          {m.cmd_open()}
        </MenuItem>
        <MenuItem id="peek" icon={Eye} shortcut={["Space"]} onAction={() => onPeek(item)}>
          {m.cmd_peek()}
        </MenuItem>
        <MenuSeparator />
        <SubmenuTrigger>
          <MenuItem id="move">{m.board_move_to()}</MenuItem>
          <Menu aria-label={m.board_move_to()} placement="right top">
            <MenuSection>
              {groups
                .filter((g) => g.key !== group.key)
                .map((g) => (
                  <MenuItem key={g.key} id={g.key} onAction={() => onMove(g, [item.id], null, { focus: true })}>
                    {g.label}
                  </MenuItem>
                ))}
            </MenuSection>
          </Menu>
        </SubmenuTrigger>
        {rankSorted ? (
          <>
            <MenuItem id="up" icon={ArrowUp} shortcut={["Alt", "up"]} onAction={() => onReorder(item, -1)}>
              {m.board_move_up()}
            </MenuItem>
            <MenuItem id="down" icon={ArrowDown} shortcut={["Alt", "down"]} onAction={() => onReorder(item, 1)}>
              {m.board_move_down()}
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </MenuTrigger>
  );
}
